import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ShieldCheck, Mail, Smartphone, Check } from 'lucide-react';
import { meApi } from '../../api/endpoints/me';
import type { MfaUserMethodLocal } from '../../api/types/MeLocal';
import { Button, Loading } from '@/kit';

type Method = 'TOTP' | 'EMAIL' | 'SMS';

const META: Record<Method, { label: string; desc: string; Icon: typeof ShieldCheck; to: string }> =
    {
        TOTP: {
            label: 'Aplicación de autenticación',
            desc: 'Un código que genera tu app (Google Authenticator, Authy, etc.).',
            Icon: ShieldCheck,
            to: '/me/security/mfa-totp-setup',
        },
        EMAIL: {
            label: 'Código por correo',
            desc: 'Te enviamos un código a tu email en cada inicio de sesión.',
            Icon: Mail,
            to: '/me/security/mfa-email-setup',
        },
        // DEC-RBAC-084: SMS como 3er método (solo disponible si el user tiene teléfono — el backend
        // lo lista en allowedMethods). Activar SMS no re-verifica (choose-sms).
        SMS: {
            label: 'Código por SMS',
            desc: 'Te enviamos un código a tu teléfono en cada inicio de sesión.',
            Icon: Smartphone,
            to: '/me/security/mfa-sms-setup',
        },
    };

// Orden de render canónico de las tarjetas.
const ALL_METHODS: Method[] = ['TOTP', 'EMAIL', 'SMS'];

// El backend usa el enum MfaMethod (EMAIL_OTP | TOTP | SMS_OTP); la UI maneja EMAIL | TOTP | SMS.
const toUiMethod = (m: MfaUserMethodLocal): Method =>
    m === 'TOTP' ? 'TOTP' : m === 'SMS_OTP' ? 'SMS' : 'EMAIL';

export function MfaPreferenceForm() {
    const navigate = useNavigate();
    // `allowedMethods` (data-driven, reemplaza el hardcode isPlatform): solo se ofrecen los métodos
    // que la security policy del tenant permite a este usuario. `currentMethod` marca la tarjeta "Actual".
    const optionsQ = useQuery({
        queryKey: ['me', 'security', 'mfa', 'options'],
        queryFn: () => meApi.getMfaOptions(),
    });

    if (optionsQ.isLoading) return <Loading />;

    // Fallback FAIL-OPEN: ante error de fetch o `allowedMethods` vacío/ausente, ofrecemos AMBOS
    // métodos. Nunca dejamos al usuario sin poder gestionar su MFA por una falla de este endpoint
    // (el backend re-enforza la policy real al intentar cambiar de método de todas formas).
    const allowedRaw = optionsQ.data?.allowedMethods ?? [];
    const allowed: Method[] = allowedRaw.length > 0 ? allowedRaw.map(toUiMethod) : ALL_METHODS;
    const methods = ALL_METHODS.filter((m) => allowed.includes(m));

    // `currentMethod` ('EMAIL_OTP' | 'TOTP' | 'SMS_OTP') es SIEMPRE el método de verificación activo del
    // user: EMAIL_OTP es el 2º factor base (el login challenge-ea por correo cuando no hay TOTP/SMS) y
    // pasa a TOTP o SMS si el user eligió ese método. Marcamos ese método como "Actual".
    const current: Method = optionsQ.data ? toUiMethod(optionsQ.data.currentMethod) : 'EMAIL';

    return (
        <div className="grid gap-3 sm:grid-cols-2">
            {methods.map((m) => {
                const isCurrent = m === current;
                const { label, desc, Icon, to } = META[m];
                return (
                    <div
                        key={m}
                        className={`flex flex-col rounded-lg border p-4 ${
                            isCurrent ? 'border-brand-accent bg-brand-accent/5' : 'border-border'
                        }`}
                    >
                        <div className="flex items-start gap-3">
                            <Icon className="mt-0.5 size-5 shrink-0 text-brand-accent" />
                            <div className="flex-1 space-y-0.5">
                                <div className="text-sm font-medium">{label}</div>
                                <p className="text-xs text-muted-foreground">{desc}</p>
                            </div>
                            {isCurrent && (
                                <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-brand-accent">
                                    <Check className="size-4" /> Actual
                                </span>
                            )}
                        </div>
                        {!isCurrent && (
                            <Button
                                variant="outline"
                                className="mt-4 w-full"
                                onClick={() => {
                                    void navigate({ to });
                                }}
                            >
                                Usar este método
                            </Button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
